import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import type { Image3D } from '~/domain/patient/image3d';
import type { ImportResult } from '~/domain/services/dicomProcessor';
import { useBumpDicomProcessorVersion } from '~/hooks/useDicomProcessorVersion';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { connectorPatientManifestState } from '~/state/atoms/connectorManifestAtoms';
import {
    connectorViewerOpenPhaseState,
    dicomProcessorState,
} from '~/state/atoms/dicomProcessorAtoms';
import { viewportReadyState } from '~/state/atoms/engineAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { useSelectStep } from '~/routes/workflowStepParam';
import { intendedObjects } from './intendedObjects';
import {
    pendingViewerOpenState,
    type PendingViewerOpen,
} from './pendingViewerOpenAtom';
import { manifestsBySeriesUid } from './streamingPlan';
import { useSliceStreaming } from './useSliceStreaming';
import { useOpenNotify } from './useOpenNotify';

/** Cap on the wait for the viewport; expiry reports a failed load. */
export const VIEWPORT_READY_TIMEOUT_MS = 30_000;

/**
 * Whether a commit produced anything the viewer can put on screen. A
 * patient row alone does not count: `toRepository` adds it before
 * reconstructing series.
 */
function hasLoadableContent(result: ImportResult): boolean {
    return (
        result.images.length > 0 ||
        result.structureSets.length > 0 ||
        result.doses.length > 0
    );
}

/**
 * The viewer half of "open in viewer": parses, commits and renders the files
 * the worklist retrieved.
 *
 * It runs here so the commit and the load are not separated by a navigation,
 * and so the commit's questions are asked where their subject is visible.
 * The exclusive-feed lock arrives already held and is released on every
 * exit; an unmount mid-load holds it until the uninterruptible parse
 * settles, rolls the commit back, then releases.
 */
export function useViewerOpenLoader(): void {
    const [pending, setPending] = useRecoilState(pendingViewerOpenState);
    const [, setPhase] = useRecoilState(connectorViewerOpenPhaseState);
    const [, setConnectorManifest] = useRecoilState(
        connectorPatientManifestState
    );
    const viewportReady = useRecoilValue(viewportReadyState);
    const { sessionId } = useParams<{ sessionId?: string }>();
    const { processImportedFiles } = useProcessDicomFiles();
    const { initializeEnvironment } = useViewportEnvironment();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const notify = useOpenNotify();
    const bumpDicomProcessorVersion = useBumpDicomProcessorVersion();
    const { startStreaming } = useSliceStreaming();
    const selectStep = useSelectStep();

    const [request, setRequest] = useState<PendingViewerOpen | null>(null);

    const viewportSettleRef = useRef<((ready: boolean) => void) | null>(null);
    const viewportReadyRef = useRef(viewportReady);
    const loadInFlightRef = useRef(false);
    const processImportedFilesRef = useRef(processImportedFiles);
    const initializeEnvironmentRef = useRef(initializeEnvironment);
    const startStreamingRef = useRef(startStreaming);
    const selectStepRef = useRef(selectStep);
    useEffect(() => {
        processImportedFilesRef.current = processImportedFiles;
        initializeEnvironmentRef.current = initializeEnvironment;
        startStreamingRef.current = startStreaming;
        selectStepRef.current = selectStep;
    });

    useEffect(() => {
        viewportReadyRef.current = viewportReady;
        if (!viewportReady) {
            return;
        }
        viewportSettleRef.current?.(true);
    }, [viewportReady]);

    /**
     * Waits for the viewport, bounded: there is no control on screen to
     * escape the wait, and a phase left non-idle refuses every other DICOM
     * import for the rest of the session.
     */
    const awaitViewport = useCallback(
        (): Promise<boolean> =>
            new Promise<boolean>((resolve) => {
                if (viewportReadyRef.current) {
                    resolve(true);
                    return;
                }
                const timer = setTimeout(
                    () => viewportSettleRef.current?.(false),
                    VIEWPORT_READY_TIMEOUT_MS
                );
                viewportSettleRef.current = (ready: boolean) => {
                    viewportSettleRef.current = null;
                    clearTimeout(timer);
                    resolve(ready);
                };
            }),
        []
    );

    /**
     * Undoes a commit that never reached the screen: left in place, its SOP
     * UIDs stay marked processed (re-opening parses to nothing) and nothing
     * on screen can clear it.
     *
     * Scoped to what the commit created: `result.patients` lists exactly the
     * patients `toRepository` added, none when it merged into a loaded
     * patient. A created patient the failed load managed to select is kept:
     * it is on screen and reachable, and deleting it under the viewport
     * would be worse. Removing the processor data un-marks the SOP UIDs.
     *
     * The commit's version bump rebuilt the image projections with the
     * removed patient's series; removal is a processor mutation like any
     * other, so it must bump too or those rows outlive their data.
     */
    const rollBackUnrenderedCommit = useRecoilCallback(
        ({ snapshot }) =>
            (result: ImportResult): void => {
                const selected = snapshot
                    .getLoadable(selectedPatientIdState)
                    .getValue();
                let removed = false;
                for (const patient of result.patients) {
                    if (patient.PatientID === selected) {
                        continue;
                    }
                    dicomProcessor.removePatientData(patient.PatientID);
                    const committed = patientRepository.getByPatientId(
                        patient.PatientID
                    );
                    if (committed) {
                        patientRepository.delete(committed.id);
                    }
                    removed = true;
                }
                if (removed) {
                    bumpDicomProcessorVersion();
                }
            },
        [dicomProcessor, patientRepository, bumpDicomProcessorVersion]
    );

    // Consume-and-clear: the handoff leaves the atom before any work starts,
    // so it can never run twice. A clinical session route discards it and
    // releases the lock; the worklist is unreachable from a session.
    useEffect(() => {
        if (!pending) {
            return;
        }
        setPending(null);
        if (sessionId !== undefined) {
            setPhase('idle');
            return;
        }
        setRequest(pending);
    }, [pending, setPending, setPhase, sessionId]);

    // Releases on unmount unless a load is in flight; the parse cannot be
    // interrupted, so its own continuation releases on settle.
    useEffect(() => {
        return () => {
            if (!loadInFlightRef.current) {
                setPhase('idle');
            }
        };
    }, [setPhase]);

    useEffect(() => {
        if (!request) {
            return;
        }
        let cancelled = false;
        // Every exit funnels here, mounted or not.
        const release = (): void => {
            loadInFlightRef.current = false;
            if (!cancelled) {
                setRequest(null);
            }
            setPhase('idle');
        };

        const load = async (): Promise<void> => {
            loadInFlightRef.current = true;
            setPhase('processing');
            // A legitimate feed while the lock it set is held.
            const result = await processImportedFilesRef.current(
                request.files,
                {
                    holdsOpenLock: true,
                    // Empty unless the worklist prepared a progressive open;
                    // an empty map leaves every series on the parsed path.
                    imageManifests: manifestsBySeriesUid(request.streaming),
                }
            );
            if (cancelled) {
                // The viewer is gone; nothing can render this commit.
                if (result) {
                    rollBackUnrenderedCommit(result);
                }
                release();
                return;
            }
            if (!result) {
                // Declined (the conflict dialog answered "discard") or threw;
                // a throw has already reported itself. Say nothing opened, so
                // an Open never resolves into silence.
                notify('patientList.open.notOpened', 'warning');
                release();
                return;
            }
            // A commit can succeed and yield nothing openable; the load
            // silently no-ops on empty collections and would read as a
            // loaded study in an empty viewer.
            if (!hasLoadableContent(result)) {
                notify('patientList.open.nothingLoadable', 'error');
                rollBackUnrenderedCommit(result);
                release();
                return;
            }

            setPhase('loading');
            // Published before the environment initializes: initialization
            // selects the patient, which creates the PatientSession, and
            // hydration must find the manifest already in place. A load
            // that fails after this point clears it again below.
            setConnectorManifest(request.connectorManifest ?? null);
            let primaryImage: Image3D | undefined;
            try {
                if (!(await awaitViewport())) {
                    throw new Error('viewport never became ready');
                }
                // The intent's objects, not the environment's own defaults.
                // Applied through initializeEnvironment rather than after it:
                // its heuristic runs as part of selecting the patient, so a
                // correction applied afterwards would be a visible flicker
                // from one image to another.
                primaryImage = await initializeEnvironmentRef.current(
                    result,
                    intendedObjects(
                        request.intent,
                        request.connectorManifest,
                        result
                    )
                );
            } catch {
                if (!cancelled) {
                    notify('patientList.open.loadFailed', 'error');
                }
                setConnectorManifest(null);
                rollBackUnrenderedCommit(result);
                release();
                return;
            }
            // The images are on screen; their remaining slices can start
            // arriving. Only after a successful load: streaming into a volume
            // no view holds would fetch pixels nobody can see, and a rolled
            // back commit has no volume at all. The primary the load chose
            // streams first.
            if (!cancelled && request.streaming.length > 0) {
                startStreamingRef.current(
                    result.images,
                    request.streaming,
                    primaryImage?.seriesUid
                );
            }
            // Only a successful load reports a partial result, and it must
            // not auto-hide: the loaded images are partly interpolated.
            if (!cancelled && request.skipped > 0) {
                notify(
                    'patientList.open.someSkipped',
                    'warning',
                    { count: request.skipped },
                    true
                );
            }
            // Last, and only on a load that stood: the step is a claim about
            // what is on screen. Asking for one the data cannot support is
            // harmless — the request stands in the URL until it can be
            // honoured — but asking after a rolled back commit would point at
            // a patient that is not there.
            if (!cancelled && request.intent?.workflow) {
                selectStepRef.current(request.intent.workflow);
            }
            release();
        };

        void load();
        return () => {
            cancelled = true;
            // Settles a wait parked on the viewport this unmount takes down.
            viewportSettleRef.current?.(false);
        };
    }, [
        request,
        setPhase,
        setConnectorManifest,
        notify,
        awaitViewport,
        rollBackUnrenderedCommit,
    ]);
}
